/** Public API contract shared by the PWA and the `public-api` Edge Function. */
import { z } from 'zod';
import { SLUG_RE } from './business-schema.ts';
import { IDEMPOTENCY_RE, TOKEN_RE } from './access-token.ts';

const slug = z.string().regex(SLUG_RE);
const uuid = z.string().uuid();
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const slotsRequest = z.object({ slug, service_id: uuid, from: day, days: z.number().int().min(1).max(14).default(7) });

export const bookRequest = z.object({
  slug,
  service_id: uuid,
  starts_at: z.string().datetime({ offset: true }),
  name: z.string().trim().min(1, 'Укажите имя').max(80),
  phone: z.string().trim().min(6, 'Укажите телефон').max(24),
  car: z.string().trim().min(2, 'Укажите автомобиль').max(80),
  car_plate: z.string().trim().max(16).default(''),
  comment: z.string().trim().max(500).default(''),
  idempotency_key: z.string().regex(IDEMPOTENCY_RE),
});
export type BookRequest = z.input<typeof bookRequest>;

export const tokenRequest = z.object({ slug, token: z.string().regex(TOKEN_RE) });
export const cancelRequest = tokenRequest.extend({ reason: z.string().max(300).optional() });
export const pushSubscribeRequest = tokenRequest.extend({
  subscription: z.object({
    endpoint: z.string().url().startsWith('https://'),
    keys: z.object({ p256dh: z.string().min(10).max(200), auth: z.string().min(8).max(100) }),
  }),
});

export const assistantRequest = z.object({
  slug,
  mode: z.enum(['client', 'owner']),
  message: z.string().trim().min(1).max(600),
  history: z
    .array(z.object({ role: z.enum(['user', 'assistant']), content: z.string().max(2000) }))
    .max(10)
    .default([]),
});
export type AssistantRequest = z.input<typeof assistantRequest>;

export interface Storefront {
  tenant: { id: string; slug: string; status: 'preview' | 'live'; timezone: string; currency: string };
  profile: {
    name: string; short_name: string; tagline: string; description: string; accent_color: string;
    phone: string; address: string; map_url: string | null; telegram: string | null; how_to_find: string | null;
    logo_path: string | null; icon_path: string | null; hero_path: string | null; hero_alt: string;
    info_cards: Array<{ title: string; text: string; icon: string }>;
    cancel_min_hours_before: number; reminder_hours_before: number; horizon_days: number;
    assistant_enabled: boolean; assistant_examples: string[];
  };
  services: Array<{ id: string; name: string; description: string; category: string; duration_minutes: number;
    price_cents: number; price_is_from: boolean; image_path: string | null }>;
  gallery: Array<{ id: string; image_path: string; caption: string }>;
  hours: Array<{ isodow: number; opens: string; closes: string }>;
  closed_days: string[];
  meta: { image_base_url: string; vapid_public_key: string | null; assistant_available: boolean; push_delivery: 'real' | 'preview_suppressed' };
}

export interface Slot { starts_at: string; day: string; available: boolean; reason: 'busy' | 'too_soon' | null }

export interface ClientBooking {
  id: string; public_code: string; status: 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  service_name: string; starts_at: string; ends_at: string; price_cents: number; price_is_from: boolean; currency: string;
  car: string; car_plate: string; customer_name: string; resource_name: string | null;
  can_cancel: boolean; cancel_deadline: string; is_demo: boolean;
}

export interface BookResponse { booking: ClientBooking; access_token: string; replayed: boolean }

export interface AssistantResponse {
  reply: string;
  mode: 'llm' | 'llm_json' | 'fallback';
  tools_used: string[];
  notice?: string;
}

export const ERROR_TEXT: Record<string, string> = {
  slot_unavailable: 'Это время только что заняли. Выберите другое.',
  idempotency_conflict: 'Запрос уже отправлялся с другими данными. Обновите страницу.',
  too_soon: 'На это время записаться уже нельзя — выберите время позже.',
  not_an_acceptance_time: 'Студия не принимает автомобили в это время.',
  beyond_horizon: 'Запись на эту дату ещё не открыта.',
  too_late_to_cancel: 'Отменить онлайн уже нельзя — позвоните в студию.',
  booking_not_found: 'Запись не найдена.',
  tenant_not_found: 'Студия не найдена.',
  rate_limited: 'Слишком много запросов. Подождите минуту.',
  bad_phone: 'Проверьте номер телефона.',
  network: 'Нет соединения. Проверьте интернет и попробуйте снова.',
  internal: 'Что-то пошло не так. Попробуйте ещё раз.',
};
